import { Button, HStack, Icon, IconButton, Stack, Text } from '@chakra-ui/react';
import { SwitchHorizontalIcon } from '@heroicons/react/solid';
import { Link, useParams } from 'react-router-dom';

import { routes } from '@blockpulse3/data/shared';

import { useGetCompanyQuery } from '@blockpulse3/graphql/hooks';
import { CompanyIdentityAvatar, UserIdentityAvatar } from '@blockpulse3/ui/commons';
import { useUser } from '@blockpulse3/web-client/auth';
import { useWorkspaceSwitcher } from './provider';

type Props = unknown;

/**
 * SwitcherButton.
 * Component rendering button to switch the workspace.
 *
 * @param {Props}
 * @returns {JSX.Element}
 */
export function SwitcherButton(): JSX.Element {
  const { user } = useUser();

  const { companyId = '' } = useParams();
  const companyReq = useGetCompanyQuery({
    variables: { companyId },
    skip: !companyId,
  });
  const company = companyReq?.data?.company;

  const { onSwitcherOpen } = useWorkspaceSwitcher();

  const homeRoute = companyId && company ? routes.workspace.fmt(companyId) : routes.me.href;

  return (
    <HStack justifyContent="space-between">
      <Button as={Link} bg="transparent" p="0" to={homeRoute}>
        <HStack pl="0.5" w="full">
          {companyId && company ? (
            <>
              <CompanyIdentityAvatar src={company.identity?.profilePicture} />
              <Stack alignItems="flex-start" flexGrow="1" pl="0.5" spacing="0.5">
                <Text
                  fontSize="md"
                  fontWeight="semibold"
                  maxWidth="150px"
                  overflow="hidden"
                  textAlign="left"
                  variant="ellipsis"
                >
                  {company?.name}
                </Text>
                <HStack alignItems="flex-start" spacing="1">
                  <UserIdentityAvatar
                    boxSize="4"
                    src={user?.individualIdentity?.identity?.profilePicture}
                  />
                  <Text
                    fontSize="sm"
                    fontWeight="medium"
                    maxWidth="130px"
                    overflow="hidden"
                    textAlign="left"
                    variant="ellipsis"
                  >
                    {`${user?.individualIdentity?.firstName} ${user?.individualIdentity?.lastName}`}
                  </Text>
                </HStack>
              </Stack>
            </>
          ) : (
            <>
              <UserIdentityAvatar src={user?.individualIdentity?.identity?.profilePicture} />
              <Stack alignItems="flex-start" flexGrow="1" pl="0.5" spacing="0">
                <Text
                  fontSize="md"
                  fontWeight="semibold"
                  maxWidth="150px"
                  overflow="hidden"
                  textAlign="left"
                  variant="ellipsis"
                >
                  {`${user?.individualIdentity?.firstName} ${user?.individualIdentity?.lastName}`}
                </Text>
              </Stack>
            </>
          )}
        </HStack>
      </Button>
      <Button
        as={IconButton}
        data-cy="workspace-switcher"
        icon={<Icon as={SwitchHorizontalIcon} />}
        variant="workspace"
        onClick={onSwitcherOpen}
      />
    </HStack>
  );
}

export type WorkspaceSwitcherButtonProps = Props;
